"use client";

import { motion } from "framer-motion";
import { useMotion } from "@/contexts/MotionContext";

const STEPS = [
  {
    number: "01",
    title: "Strategy Sprint",
    description: "Understand your value proposition and core engagement loop.",
    color: "brand-teal",
    component: (isAnimating: boolean) => (
      <motion.div
        className="w-16 h-16 flex items-center justify-center"
        animate={
          isAnimating
            ? {
                scale: [1, 1.05, 1],
              }
            : undefined
        }
        transition={{ duration: 2, repeat: Infinity }}
      >
        <motion.div
          className="w-12 h-12 border-4 border-brand-teal rounded-full"
          animate={
            isAnimating
              ? {
                  rotate: 360,
                }
              : undefined
          }
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        >
          <motion.div
            className="w-3 h-3 bg-brand-teal rounded-full absolute"
            style={{ top: "-6px", left: "calc(50% - 6px)" }}
          />
        </motion.div>
      </motion.div>
    ),
  },
  {
    number: "02",
    title: "Design In Code",
    description:
      "Get to working software fast, without compromising aesthetics.",
    color: "brand-purple",
    component: (isAnimating: boolean) => (
      <motion.div
        className="w-16 h-16 flex items-center justify-center"
        animate={
          isAnimating
            ? {
                opacity: [0.5, 1, 0.5],
              }
            : undefined
        }
        transition={{ duration: 2, repeat: Infinity }}
      >
        <motion.div className="flex gap-1">
          <motion.div
            className="w-2 h-10 bg-brand-purple rounded-sm"
            animate={
              isAnimating
                ? {
                    x: [-4, 0, -4],
                  }
                : undefined
            }
            transition={{ duration: 2, repeat: Infinity }}
          />
          <motion.div
            className="flex flex-col gap-1 justify-center"
            animate={
              isAnimating
                ? {
                    opacity: [0.5, 1, 0.5],
                  }
                : undefined
            }
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-6 h-1.5 bg-brand-purple/60 rounded-full" />
            <div className="w-4 h-1.5 bg-brand-purple/80 rounded-full" />
            <div className="w-5 h-1.5 bg-brand-purple rounded-full" />
          </motion.div>
          <motion.div
            className="w-2 h-10 bg-brand-purple rounded-sm"
            animate={
              isAnimating
                ? {
                    x: [4, 0, 4],
                  }
                : undefined
            }
            transition={{ duration: 2, repeat: Infinity }}
          />
        </motion.div>
      </motion.div>
    ),
  },
  {
    number: "03",
    title: "Launch & Learn",
    description: "Ship to users quickly, iterate based on real feedback.",
    color: "brand-pink",
    component: (isAnimating: boolean) => (
      <motion.div
        className="w-16 h-16 flex items-center justify-center"
        animate={
          isAnimating
            ? {
                y: [0, -8, 0],
              }
            : undefined
        }
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <motion.div
          className="relative"
          animate={
            isAnimating
              ? {
                  y: [-4, -12, -4],
                }
              : undefined
          }
          transition={{ duration: 2, repeat: Infinity }}
        >
          <motion.div
            className="w-8 h-8 bg-brand-pink"
            style={{
              clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
            }}
          />
          <motion.div
            className="absolute -bottom-2 left-1/2 -translate-x-1/2"
            animate={
              isAnimating
                ? {
                    opacity: [0, 1, 0],
                    scale: [0.5, 1, 0.5],
                  }
                : undefined
            }
            transition={{ duration: 1, repeat: Infinity }}
          >
            <div className="w-4 h-4 bg-brand-pink/20 rounded-full" />
            <div className="w-3 h-3 bg-brand-pink/40 rounded-full mt-1" />
            <div className="w-2 h-2 bg-brand-pink/60 rounded-full mt-1" />
          </motion.div>
        </motion.div>
      </motion.div>
    ),
  },
];

export default function ProcessSteps() {
  const { isMotionEnabled } = useMotion();

  return (
    <div className="grid md:grid-cols-3 gap-8 relative">
      {STEPS.map((step, index) => (
        <motion.div
          key={index}
          className="relative py-6 px-6 rounded-3xl bg-card-texture bg-repeat"
          initial={isMotionEnabled ? { opacity: 0, y: 20 } : undefined}
          whileInView={isMotionEnabled ? { opacity: 1, y: 0 } : undefined}
          transition={{ delay: index * 0.2 }}
          viewport={{ once: true }}
        >
          {/* Icon */}
          <div className="mx-auto mb-6 flex justify-center">
            {step.component(isMotionEnabled)}
          </div>

          {/* Content */}
          <div className="text-center relative z-10">
            <div className={`text-2xl font-medium mb-3`}>{step.number}</div>
            <h3 className="text-xl font-medium mb-2">{step.title}</h3>
            <p className="text-base text-gray-600 dark:text-gray-300">
              {step.description}
            </p>
          </div>
        </motion.div>
      ))}
    </div>
  );
}
